import {
  Dashboard,
  LocationOn,
  ViewQuilt,
  WebAsset,
} from '@mui/icons-material';

import { NavMenu } from './@types/global';
import routes from './routes/routes.constants';
import { showWatchlist } from './utils/features';

export const APP_NAME = 'VideaAI';
export const RETRY_MAX_ATTEMPTS = 3;
export const DEFAULT_CONTRAST_VALUE = 1;
export const MIN_CONTRAST_VALUE = -500;
export const MAX_CONTRAST_VALUE = 200;
export const DEFAULT_BRIGHTNESS_VALUE = 255;
export const MIN_BRIGHTNESS_VALUE = 100;
export const MAX_BRIGHTNESS_VALUE = 1600;
export const RETRY_STATUS_CODES = [500, 502, 503];
export const NONE = 'NONE';
export const LOADING = 'LOADING';
export const ERROR = 'ERROR';
export const NULL_CHARACTER = '\u0000';
// the folder to watch for updates
export const WORKING_DIRECTORY = 'working.directory';
export const UNIQUE_PATIENT_UPLOAD_COUNT = 'patient.uniqueUploadCount';
export const DAILY_UNIQUE_PATIENT_UPLOAD_COUNT =
  'patient.dailyUniqueUploadCount';
export const TWO_DAYS_IN_MILLIS = 86400000 * 2;
export const USER_ID = 'user.id';
export const FILE_ADDED = 'file-added';
export const FILE_CHANGED = 'file-changed';
export const CHANGE_WORKING_DIRECTORY = 'CHANGE_WORKING_DIRECTORY';
export const PATIENT_UPLOAD_COMPLETE = 'PATIENT_UPLOAD_COMPLETE';
export const SUPPORTED_MODEL_TYPES = [
  'caries',
  'radiographic_boneloss',
  'periapical_radiolucency',
  'calculus',
  'restorative_adjacent_lucency',
];

export const CARIES = 'caries';
export const PERIAPICAL_RADIOLUCENCY = 'periapical_radiolucency';
export const RADIOGRAPHIC_BONELOSS = 'radiographic_boneloss';
export const CALCULUS = 'calculus';
export const CARIES_SUBCLASSIFICATION = 'caries_subclassification';
export const AREA_OF_CONCERN = 'area_of_concern';
export const RESTORATIVE_ADJACENT_LUCENCY = 'restorative_adjacent_lucency';
export const VIDEA_DENTAL_ASSIST_CARIES = 'VIDEA_DENTAL_ASSIST_CARIES';
export const VIDEA_DENTAL_ASSIST_PERIAPICAL_RADIOLUCENCY =
  'VIDEA_DENTAL_ASSIST_PERIAPICAL_RADIOLUCENCY';
export const VIDEA_DENTAL_ASSIST_CALCULUS = 'VIDEA_DENTAL_ASSIST_CALCULUS';

export const INSURANCE_CLAIM_SUMMARY_ERROR_MESSAGE =
  'Sorry, could not generate an insurance claim summary. Please try again later';
export const SENTRY_DSN =
  'https://d90364dbb5c74811b50794049544134a@o314447.ingest.sentry.io/1799723';

export const SENTRY_TRACE_SAMPLE_RATE = 0.05;

export const ELASTIC_APM_DISTRIBUTED_TRACING_ORIGINS = [
  // local
  'http://localhost:3000',  
  
  // dev  
  'https://nucleus-api.videahealth.io',
  'https://nucleus-api.dev.videahealth.io',
  'https://assist.dev.videahealth.io',

  // prod
  'https://app.videa.ai',
  'https://app.videahealth.io',
  'https://assist.prod.videahealth.io',
];
export const ELASTIC_APM_SERVER_URL = 'https://7e43a6408efa47d397f1882dfa2f98fa.apm.us-east-2.aws.elastic-cloud.com:443';
export const ELASTIC_APM_SERVICE_NAME = 'videa-ai-ui';

export const FDA_BUILD = 'FDA_BUILD';

export const NON_FDA_BUILD = 'NON_FDA_BUILD';

export const DSO_ID = 'dso.id';
export const PRACTICE_ID = 'practice.id';
export const PATIENT_INFO_XML = 'PatientInfo.xml';
export const IMAGES_XML = 'Images.xml';

export const DICOM_FILE_TYPES = ['dcm', 'dicom', 'dic', 'sc', 'stv'];
export const IMAGES_WITH_METADATA = ['rvg', ...DICOM_FILE_TYPES];
export const VALID_FILE_TYPES_ARRAY = [
  'tiff',
  'tif',
  'png',
  'jpeg',
  'jpg',
  'dex',
  ...IMAGES_WITH_METADATA,
];
export const PPD_VALID_FILE_TYPES_ARRAY = [
  'dex',
  'tiff',
  'tif',
  ...IMAGES_WITH_METADATA,
];

export const SUPPORTED_XML_PARAMS = [
  // PatientInfo.xml params
  'internal_id',
  'first_name',
  'last_name',
  'sex',
  'age',
  'birth_date',
  'directory',
  'file_name',
  'type',
  'anatomic_region_sequence',
  'primary_anatomic_structure_sequence',
  'retaken',
  'imageIndex',
  'exam_complete',
  'examComplete',
];

export const MAC_DELIMITER = '/';
export const WINDOWS_FILE_DELIMITER = '\\';

export const SHOW_VALIDATION_DROP_DOWN_MENUS_RULE_NAME =
  'SHOW_VALIDATION_DROP_DOWN_MENUS';

// Colors
export const BLUE_COLOR = '#1890ff';
export const BITEWING = 'BITEWING';

export const videaInsightsMenuOptions = [
  {
    name: NavMenu.DASHBOARD,
    icon: Dashboard,
    path: routes.VIDEA_INSIGHT_DASHBOARD,
  },
  {
    name: 'Locations',
    icon: LocationOn,
    path: routes.VIDEA_INSIGHT_LOCATIONS,
  },
];

export const videaInsightsAssistMenuBar = [
  {
    name: 'Videa Insights',
    icon: ViewQuilt,
    path: routes.VIDEA_INSIGHT_DASHBOARD,
  },
  {
    name: 'Videa AI',
    icon: WebAsset,
    path: routes.UPLOADER,
  },
];

export const DEBOUNCE_TIME_DELAY = 2000;

export const placeHolders = {
  locations: {
    search: 'Search by location name',
  },
  watchlist: {
    add: 'Add to Watchlist',
    remove: 'Remove from Watchlist',
  },
};

export const OpsDashboardText = {
  TOTAL_AI_FINDINGS: 'AI Findings',
  TOTAL_AI_FINDINGS_INFO:
    '# of caries discovered through Videa AI within your Organization.',
  TOTAL_AI_FINDINGS_INFO_LOC:
    '# of caries discovered through Videa AI within this Location.',
  TREATMENTS_PROPOSED: 'Treatments Presented',
  TREATMENTS_PROPOSED_INFO:
    'Treatments that are currently Proposed, but not yet Scheduled, or Completed.',
  TREATMENTS_PROPOSED_WITH_PLANNED_INFO:
    'Treatments that are currently Proposed, but not yet Planned, or Completed.',
  TREATMENTS_SCHEDULED: 'Treatments Scheduled',
  TREATMENTS_SCHEDULED_INFO:
    'Treatments that are currently Scheduled but not Completed.',
  TREATMENTS_COMPLETED: 'Treatments Completed',
  TREATMENTS_COMPLETED_INFO: 'Treatments that have been Completed.',
  AI_DIAGNOSIS_RATE: 'AI Diagnosis Score',
  AI_DIAGNOSIS_RATE_INFO:
    'This is a score between 0 and 1 that illustrates how many AI Findings become accepted Treatments.',
  CASE_ACCEPTANCE_RATE: 'Case Acceptance',
  CASE_ACCEPTANCE_RATE_INFO:
    'The % of Treatments Completed out of total Treatments Presented.',
  CASE_ACCEPTANCE_RATE_WITH_PLANNED_INFO:
    'The % of Treatments Completed out of total Treatments Planned.',
  FLAG_INFO:
    'Displays potential mentorship opportunities for a given location.',
  RESTORATIVE_REVENUE_INFO: 'This is inclusive of all restorative ADA codes.',
  TREATMENTS_PLANNED: 'Treatments Planned',
  TREATMENTS_PLANNED_INFO:
    'Treatments that are currently Planned but not Completed.',
};

export interface LocationsHeadCell {
  id: string;
  label: string;
  numeric: boolean;
  width: string;
  hideSortIcon?: boolean;
  message?: string;
}

export const CAPTION_FONT = 'proxima-nova, sans-serif';

export const locationsHeadCells: Array<LocationsHeadCell> = [
  {
    id: 'location',
    label: 'Location',
    numeric: false,
    width: showWatchlist() ? '30%' : '18%',
    hideSortIcon: true,
  },
  // Will be added in future
  // {
  //   id: 'dentists',
  //   label: 'Dentists',
  //   numeric: true,
  //   width: '5%',
  // },
  // {
  //   id: 'hygienists',
  //   label: 'Hygienists',
  //   numeric: true,
  //   width: '5%',
  // },
  {
    id: 'aiFindings',
    label: 'AI Findings',
    numeric: true,
    width: showWatchlist() ? '20%' : '11.5%',
    hideSortIcon: true,
    message: OpsDashboardText.TOTAL_AI_FINDINGS_INFO,
  },
  {
    id: 'treatmentsProposed',
    label: 'Treatments Presented',
    numeric: true,
    width: showWatchlist() ? '20%' : '11.5%',
    hideSortIcon: true,
    message: OpsDashboardText.TREATMENTS_PROPOSED_INFO,
  },
  {
    id: 'treatmentsScheduled',
    label: 'Treatments Scheduled',
    numeric: true,
    width: showWatchlist() ? '20%' : '11.5%',
    hideSortIcon: true,
    message: OpsDashboardText.TREATMENTS_SCHEDULED_INFO,
  },
  {
    id: 'treatmentsCompleted',
    label: 'Treatments Completed',
    numeric: true,
    width: showWatchlist() ? '20%' : '11.5%',
    hideSortIcon: true,
    message: OpsDashboardText.TREATMENTS_COMPLETED_INFO,
  },
  // {
  //   id: 'totalRevenue',
  //   label: 'Revenue',
  //   numeric: true,
  //   width: '17%',
  // },
  {
    id: 'acceptanceRate',
    label: 'Case Acceptance',
    numeric: true,
    width: showWatchlist() ? '20%' : '11.5%',
    hideSortIcon: true,
    message: OpsDashboardText.CASE_ACCEPTANCE_RATE_INFO,
  },
  {
    id: 'aiDiagnosisRate',
    label: 'AI Diagnosis Score',
    numeric: true,
    width: showWatchlist() ? '20%' : '11.5%',
    hideSortIcon: true,
    message: OpsDashboardText.AI_DIAGNOSIS_RATE_INFO,
  },
  {
    id: 'performanceFlag',
    label: 'Flag',
    numeric: true,
    width: showWatchlist() ? '20%' : '12%',
    hideSortIcon: true,
    message: OpsDashboardText.FLAG_INFO,
  },
];

if (showWatchlist()) {
  locationsHeadCells.push({
    id: 'watchlist',
    label: 'Watchlist',
    numeric: true,
    width: '10%',
    hideSortIcon: true,
  });
}

export const restrictedUrlPreventDialog = ['login', 'videa_insight'];

// points to Garrett's Dental Dream practice under Sales Demo Organization in prod
export const VIDEA_DEMO_USER_PPD = {
  organizationId: '62c44f97c1ecaa68cdd7cbc1',
  practiceId: '62c44fc6c1ecaa68cdd7cbd2',
};

export const errorOpsDashboard = {
  dashboard: {
    title: 'Something has gone wrong.',
    content:
      'Please try reloading your browser. If the issue persists, try revisiting the page later or email support@videa.ai.',
  },
};

export interface TopProviderHeadCell {
  id: string;
  label: string;
  numeric: boolean;
  width: string;
}

export const topProviderHeadCells: Array<TopProviderHeadCell> = [
  {
    id: 'name',
    label: '',
    numeric: false,
    width: '30%',
  },
  {
    id: 'treatmentsCompleted',
    label: 'Treatments Completed',
    numeric: true,
    width: '20%',
  },
  {
    id: 'revenue',
    label: 'Revenue',
    numeric: true,
    width: '25%',
  },
  {
    id: 'caseAcceptanceRate',
    label: 'Case Acceptance',
    numeric: true,
    width: '25%',
  },
];

export const defaultMeta = {
  page: 0,
  pageSize: 0,
  itemsCount: 0,
  totalPages: 0,
};

export const locationsFlagText = {
  red: 'Low Production',
  voilet: 'Low Case Acceptance',
  yellow: 'Low Concurrence',
  white: '',
};

export const videaInsightsScreens = {
  organizationProfile: 'Organization Profile',
  locationListing: 'Location Listing',
  locationDetails: 'Location Details',
};
